import { useEffect, useMemo, useState } from "react"
import { mapDesigns } from "@mono/mapDesigns"
import { allChallenges } from "@mono/allChallenges"
import { allPieces, images } from "@/utils/data"
import { gameRoles } from "@/utils/gameRoles"
import { useAppSelector } from "@/store/hooks"
import { useBackHandler } from "@/Hooks/useBackHandler"
import classNames from "classnames"
import Color from "color"
import {
  DEFAULT_TEST_MAP_ID,
  ensureInitialPreset,
  getActivePresetId,
  makePresetId,
  savePresets,
  setActivePresetId,
  type ITestSettingsPreset,
} from "@/utils/testSettingsStorage"

/* ------------------------------------------------------------------ */
/* Why this file carries a second layout                               */
/* ------------------------------------------------------------------ */
/**
 * On a desktop this is a two-column console: four player panels in a 2×2 grid
 * on the left, the whole property palette on the right, and properties move
 * between them by dragging. Two things break that on a phone, and neither is
 * fixable by shrinking it:
 *
 * 1. Space. Touch devices are locked to landscape (LandscapeOverlay), so the
 *    window is roughly 840×390 — and index.css halves the root font-size below
 *    1000px, so every rem-based size halves with it while the raw-px ones
 *    (`text-[11px]`) stay put. Six panes inside ~300px of usable height would
 *    be unreadable even if the type held. So under `mobile:` the body shows one
 *    pane at a time behind a tab bar (Game · one tab per player · Properties),
 *    and every size is restated in raw px — the repo's usual answer, see
 *    FastPaceVotePanel and ToastContainer.
 *
 * 2. Touch has no HTML5 drag. `dragstart` never fires from a finger, so the
 *    only way to assign a property simply would not work. On mobile a property
 *    card is a button instead: tapping one opens a sheet that assigns it to a
 *    player or takes it back. Hover-only remove buttons get the same treatment
 *    — there is no hover to reveal them, so they are always visible there.
 *
 * The desktop layout is unchanged. Everything below either branches on
 * `isMobile` (width < 1000, the same threshold as the `mobile:` variant) or
 * carries `mobile:` overrides.
 */

interface TestSettingsModalProps {
  isOpen: boolean
  onClose: () => void
  /** Called whenever the active preset's settings change, so the host can use them on Start Game. */
  onActiveSettingsChange: (settings: ITestNewGameSettings) => void
}

// We always expose 4 abstract player slots (orders 0-3). playerOrder is abstract in test settings.
const PLAYER_ORDERS = [0, 1, 2, 3] as PlayerOrder[]
const MAX_HOUSES = 5 // 1-4 houses, 5 = hotel

const playerColor = (order: number) => allPieces[order]?.color || "#888"

const ALL_CARD_IDS = Object.keys(images.cards) as ActionCardId[]

const prettyCardName = (id: string) =>
  id
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")

/** Which single pane the mobile layout is showing. Numbers are player orders. */
type MobileTab = "game" | "props" | PlayerOrder

/* ------------------------------------------------------------------ */
/* Shared style tokens — keep the modal on the app's warm theme        */
/* ------------------------------------------------------------------ */
// Dark field that picks up a warm amber focus ring instead of the default browser look.
const FIELD =
  "bg-black/30 border border-white/10 rounded-lg text-primary/90 placeholder:text-white/25 outline-none focus:border-[#E49E78]/60 focus:ring-2 focus:ring-[#E49E78]/[0.15] transition-colors mobile:rounded-[8px]"
// Small uppercase section/label style. Already raw px, so the root-font halving
// under 1000px leaves it alone.
const LABEL = "text-[11px] font-semibold uppercase tracking-wider text-white/50"
const BTN = "rounded-lg font-semibold transition-colors mobile:rounded-[8px]"
// The × in every header. Ten rem units collapse to 20px on a phone, well under
// any sane touch target, so the mobile step puts it back at 30px.
const CLOSE_BTN =
  "h-10 w-10 shrink-0 flex items-center justify-center rounded-lg text-white/50 hover:text-white hover:bg-white/10 text-3xl leading-none transition-colors mobile:h-[30px] mobile:w-[30px] mobile:text-[24px] mobile:rounded-[8px]"

// The lobby pins its navbar with `fixed top-0 left-0 z-[100]` below `md`
// (Pages/NewGame/index.tsx), so on a phone every layer of this modal has to
// clear 100 or the navbar paints over it. Desktop keeps the original 50/60/70,
// where that navbar is `md:static`. Toasts live at z-[300] and stay on top.
const MOBILE_Z = {
  base: "mobile:z-[110]",
  sub: "mobile:z-[120]",
  sheet: "mobile:z-[130]",
}

/* ------------------------------------------------------------------ */
/* Small property card — draggable on desktop, tappable on touch       */
/* ------------------------------------------------------------------ */
interface TestPropertyCardProps {
  cell: ICell
  group?: IPropertyGroup
  onDragStart: (order: number) => void
  onDragEnd: () => void
  compact?: boolean
  /** Off on touch: a finger cannot start an HTML5 drag, and a long press only leaves a stuck ghost. */
  draggable?: boolean
  /** Mobile's replacement for dragging — opens the assign sheet for this property. */
  onTap?: () => void
}
const TestPropertyCard: React.FC<TestPropertyCardProps> = ({
  cell,
  group,
  onDragStart,
  onDragEnd,
  compact,
  draggable = true,
  onTap,
}) => {
  const base = new Color(group?.withStrip ? group.color : group?.color || "#cccccc")
  const colorDark = base.darken(0.35)
  const colorLight = base.lighten(0.1)
  return (
    <div
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", String(cell.order))
        e.dataTransfer.effectAllowed = "move"
        onDragStart(cell.order)
      }}
      onDragEnd={onDragEnd}
      onClick={onTap}
      title={`#${cell.order} ${cell.propertyDetails?.name || ""}`}
      className={classNames(
        "group/card shrink-0 select-none rounded-md ring-1 ring-black/40 overflow-hidden flex flex-col transition-transform duration-100 hover:-translate-y-0.5 hover:ring-white/40",
        draggable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer active:brightness-110",
        // The rem widths land at 28px on a phone — small enough that the name is
        // unreadable and the tap target is a coin flip.
        compact ? "w-14 mobile:w-[52px]" : "w-16 mobile:w-[56px]"
      )}
      style={{
        backgroundImage: `radial-gradient(circle at 25% 25%, ${colorLight.hex()} 0%, ${colorDark.hex()} 100%)`,
        boxShadow: "0 2px 4px rgba(0,0,0,0.4)",
      }}
    >
      <div className="h-3 w-full mobile:h-[10px]" style={{ backgroundColor: base.hex() }} />
      <div className="flex-1 bg-white/95 px-1 py-1.5 flex flex-col items-center justify-between gap-0.5 mobile:px-[3px] mobile:py-[4px] mobile:gap-[2px]">
        <span className="text-[8px] leading-tight text-center text-black font-semibold line-clamp-2 mobile:text-[9px]">
          {cell.propertyDetails?.name || `#${cell.order}`}
        </span>
        <span className="text-[7px] text-gray-500 font-medium mobile:text-[8px]">#{cell.order}</span>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Mobile tab button                                                   */
/* ------------------------------------------------------------------ */
const MobileTabButton: React.FC<{
  active: boolean
  onClick: () => void
  children: React.ReactNode
}> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={classNames(
      "shrink-0 flex items-center gap-[6px] rounded-[8px] border px-[10px] py-[6px] text-[12px] font-semibold whitespace-nowrap transition-colors",
      active
        ? "bg-[#E49E78]/[0.15] border-[#E49E78]/50 text-primary"
        : "bg-white/[0.03] border-white/10 text-primary/70"
    )}
  >
    {children}
  </button>
)

/* ------------------------------------------------------------------ */
/* Main modal                                                         */
/* ------------------------------------------------------------------ */
const TestSettingsModal: React.FC<TestSettingsModalProps> = ({ isOpen, onClose, onActiveSettingsChange }) => {
  const isMobile = useAppSelector((state) => state.global.isMobile)
  const [presets, setPresets] = useState<ITestSettingsPreset[]>([])
  const [selectedId, setSelectedId] = useState<string>("")
  const [draft, setDraft] = useState<ITestNewGameSettings>({})
  const [cardPickerPlayer, setCardPickerPlayer] = useState<PlayerOrder | null>(null)
  const [draggingOrder, setDraggingOrder] = useState<number | null>(null)
  const [presetPickerOpen, setPresetPickerOpen] = useState(false)
  const [mobileTab, setMobileTab] = useState<MobileTab>("game")
  // The property whose assign sheet is open — mobile's stand-in for a drag.
  const [assignOrder, setAssignOrder] = useState<number | null>(null)

  useBackHandler(() => {
    onClose()
    return true
  }, isOpen)

  const activeMapId = draft.mapId || DEFAULT_TEST_MAP_ID
  const map = useMemo(
    () => mapDesigns.find((m) => m.id === activeMapId) || mapDesigns[0],
    [activeMapId]
  )
  const propertyCells = useMemo(() => map?.cells.filter((c) => c.type === "property") || [], [map])
  const groupOf = (cell: ICell) => map?.groups.find((g) => g.id === cell.propertyDetails?.groupId)
  const cellByOrder = (order: number) => propertyCells.find((c) => c.order === order)

  // Load presets on open
  useEffect(() => {
    if (!isOpen) return
    const loaded = ensureInitialPreset()
    setPresets(loaded)
    const active = getActivePresetId()
    const chosen = loaded.find((p) => p.id === active) || loaded[0]
    setSelectedId(chosen.id)
    setDraft(structuredClone(chosen.settings || {}))
    // A reopened modal starts at the front of the deck rather than wherever the
    // last visit happened to leave off.
    setMobileTab("game")
    setAssignOrder(null)
  }, [isOpen])

  // `100vh` on a phone is the height *without* the browser chrome, so a
  // full-bleed vh-sized modal hangs below the visible area and its bottom row
  // becomes unreachable. `window.innerHeight` is the height actually on screen.
  const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight)
  useEffect(() => {
    if (!isOpen) return
    const syncViewportHeight = () => setViewportHeight(window.innerHeight)
    syncViewportHeight()
    window.addEventListener("resize", syncViewportHeight)
    window.addEventListener("orientationchange", syncViewportHeight)
    window.visualViewport?.addEventListener("resize", syncViewportHeight)
    return () => {
      window.removeEventListener("resize", syncViewportHeight)
      window.removeEventListener("orientationchange", syncViewportHeight)
      window.visualViewport?.removeEventListener("resize", syncViewportHeight)
    }
  }, [isOpen])

  if (!isOpen) return null

  /* ---------------- preset management ---------------- */
  const persist = (next: ITestSettingsPreset[], activeId: string, settings: ITestNewGameSettings) => {
    setPresets(next)
    savePresets(next)
    setActivePresetId(activeId)
    onActiveSettingsChange(settings)
  }

  const handleSelectPreset = (id: string) => {
    const preset = presets.find((p) => p.id === id)
    if (!preset) return
    setSelectedId(id)
    setDraft(structuredClone(preset.settings || {}))
    setActivePresetId(id)
    onActiveSettingsChange(preset.settings || {})
  }

  // Persist current draft into the selected preset.
  const handleSave = () => {
    const next = presets.map((p) => (p.id === selectedId ? { ...p, settings: structuredClone(draft) } : p))
    persist(next, selectedId, draft)
  }

  // Save the current draft and close the modal.
  const handleSaveAndClose = () => {
    handleSave()
    onClose()
  }

  const handleNew = () => {
    const name = window.prompt("Name for the new test settings preset?", `Preset ${presets.length + 1}`)
    if (name === null) return
    const initial: ITestNewGameSettings = { mapId: DEFAULT_TEST_MAP_ID }
    const preset: ITestSettingsPreset = { id: makePresetId(), name: name.trim() || "Untitled", settings: initial }
    const next = [...presets, preset]
    setSelectedId(preset.id)
    setDraft(initial)
    persist(next, preset.id, initial)
  }

  const handleRename = () => {
    const current = presets.find((p) => p.id === selectedId)
    if (!current) return
    const name = window.prompt("Rename preset", current.name)
    if (name === null) return
    const next = presets.map((p) => (p.id === selectedId ? { ...p, name: name.trim() || p.name } : p))
    setPresets(next)
    savePresets(next)
  }

  const handleDelete = () => {
    if (!window.confirm("Delete this preset?")) return
    const next = presets.filter((p) => p.id !== selectedId)
    if (next.length === 0) {
      const fresh: ITestSettingsPreset = { id: makePresetId(), name: "Default", settings: {} }
      next.push(fresh)
    }
    setSelectedId(next[0].id)
    setDraft(structuredClone(next[0].settings || {}))
    persist(next, next[0].id, next[0].settings || {})
  }

  /* ---------------- draft mutators ---------------- */
  const update = (patch: Partial<ITestNewGameSettings>) => setDraft((d) => ({ ...d, ...patch }))

  // Changing the map invalidates property ownership/houses (cell orders differ between maps).
  const changeMap = (newMapId: string) =>
    setDraft((d) => ({ ...d, mapId: newMapId, customPropertyOwnerships: [], housesData: [] }))

  const ownerOf = (order: number): PlayerOrder | undefined =>
    draft.customPropertyOwnerships?.find((o) => o.propertyOrder === order)?.playerOrder as PlayerOrder | undefined

  const assignProperty = (order: number, playerOrder: PlayerOrder | null) => {
    setDraft((d) => {
      const others = (d.customPropertyOwnerships || []).filter((o) => o.propertyOrder !== order)
      const nextOwnerships =
        playerOrder === null ? others : [...others, { propertyOrder: order, playerOrder: playerOrder as number }]
      // If unassigned, drop any house data for it.
      const nextHouses =
        playerOrder === null ? (d.housesData || []).filter((h) => h.propertyOrder !== order) : d.housesData
      return { ...d, customPropertyOwnerships: nextOwnerships, housesData: nextHouses }
    })
  }

  const propertiesOf = (playerOrder: PlayerOrder) =>
    (draft.customPropertyOwnerships || [])
      .filter((o) => o.playerOrder === playerOrder)
      .map((o) => o.propertyOrder)

  const housesOf = (order: number) => draft.housesData?.find((h) => h.propertyOrder === order)?.houses || 0

  const setHouses = (order: number, houses: number) => {
    setDraft((d) => {
      const others = (d.housesData || []).filter((h) => h.propertyOrder !== order)
      return { ...d, housesData: houses > 0 ? [...others, { propertyOrder: order, houses }] : others }
    })
  }

  const roleOf = (playerOrder: PlayerOrder) => draft.customRoles?.[playerOrder]
  const setRole = (playerOrder: PlayerOrder, roleId: GameRoleId | undefined) =>
    setDraft((d) => {
      const roles = { ...(d.customRoles || {}) }
      if (roleId === undefined) delete roles[playerOrder]
      else roles[playerOrder] = roleId
      return { ...d, customRoles: roles }
    })

  const fundsOf = (playerOrder: PlayerOrder) => draft.customStartingFunds?.[playerOrder]
  const setFunds = (playerOrder: PlayerOrder, value: number | undefined) =>
    setDraft((d) => {
      const funds = { ...(d.customStartingFunds || {}) }
      if (value === undefined || Number.isNaN(value)) delete funds[playerOrder]
      else funds[playerOrder] = value
      return { ...d, customStartingFunds: funds }
    })

  const positionOf = (playerOrder: PlayerOrder) =>
    draft.testPlayerPositions?.find((p) => p.playerOrder === playerOrder)?.cellOrder
  const setPosition = (playerOrder: PlayerOrder, value: number | undefined) =>
    setDraft((d) => {
      const others = (d.testPlayerPositions || []).filter((p) => p.playerOrder !== playerOrder)
      return {
        ...d,
        testPlayerPositions:
          value === undefined || Number.isNaN(value)
            ? others
            : [...others, { playerOrder: playerOrder as number, cellOrder: value }],
      }
    })

  const cardsOf = (playerOrder: PlayerOrder): ActionCardId[] => draft.cards?.[playerOrder] || []
  const addCard = (playerOrder: PlayerOrder, cardId: ActionCardId) =>
    setDraft((d) => ({ ...d, cards: { ...(d.cards || {}), [playerOrder]: [...(d.cards?.[playerOrder] || []), cardId] } }))
  const removeOneCard = (playerOrder: PlayerOrder, cardId: ActionCardId) =>
    setDraft((d) => {
      const current = [...(d.cards?.[playerOrder] || [])]
      const idx = current.lastIndexOf(cardId)
      if (idx >= 0) current.splice(idx, 1)
      return { ...d, cards: { ...(d.cards || {}), [playerOrder]: current } }
    })

  // Group a player's cards by id with counts for compact display.
  const groupedCards = (playerOrder: PlayerOrder) => {
    const counts: Partial<Record<ActionCardId, number>> = {}
    for (const c of cardsOf(playerOrder)) counts[c] = (counts[c] || 0) + 1
    return Object.entries(counts) as [ActionCardId, number][]
  }

  const assignedOrders = new Set((draft.customPropertyOwnerships || []).map((o) => o.propertyOrder))
  const selectedPreset = presets.find((p) => p.id === selectedId)

  /* ---------------- shared blocks ---------------- */

  // The four game-wide controls. `stacked` is the mobile "Game" tab, where each
  // field gets a label above it; the desktop header packs the same four into
  // one inline strip.
  const renderGlobalFields = (stacked: boolean) => {
    const labelClass = stacked ? "flex flex-col gap-[5px] min-w-0" : "flex items-center gap-2"
    const width = (desktopWidth: string) => (stacked ? "w-full" : desktopWidth)
    return (
      <>
        <label className={labelClass}>
          <span className={LABEL}>Map</span>
          <select
            value={activeMapId}
            onChange={(e) => changeMap(e.target.value)}
            className={classNames(
              FIELD,
              "py-2 px-3 text-sm cursor-pointer mobile:py-[7px] mobile:px-[9px] mobile:text-[13px]",
              width("w-48")
            )}
          >
            {mapDesigns.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </label>
        <label className={labelClass}>
          <span className={LABEL}>Challenge</span>
          <select
            value={draft.challengeId || ""}
            onChange={(e) => update({ challengeId: (e.target.value || undefined) as ChallengeId | undefined })}
            className={classNames(
              FIELD,
              "py-2 px-3 text-sm cursor-pointer mobile:py-[7px] mobile:px-[9px] mobile:text-[13px]",
              width("w-60")
            )}
          >
            <option value="">None</option>
            {allChallenges.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </label>
        <label className={labelClass}>
          <span className={classNames(LABEL, "whitespace-nowrap")}>Money on table</span>
          <input
            type="number"
            inputMode="numeric"
            value={draft.moneyOnTable ?? ""}
            onChange={(e) => update({ moneyOnTable: e.target.value === "" ? undefined : Number(e.target.value) })}
            className={classNames(
              FIELD,
              "py-2 px-3 text-sm mobile:py-[7px] mobile:px-[9px] mobile:text-[13px]",
              width("w-28")
            )}
            placeholder="0"
          />
        </label>
        <label className={labelClass}>
          <span className={classNames(LABEL, "whitespace-nowrap")}>Active turn</span>
          <select
            value={draft.activeTurn ?? ""}
            onChange={(e) =>
              update({ activeTurn: e.target.value === "" ? undefined : (Number(e.target.value) as PlayerOrder) })
            }
            className={classNames(
              FIELD,
              "py-2 px-3 text-sm cursor-pointer mobile:py-[7px] mobile:px-[9px] mobile:text-[13px]",
              width("w-32")
            )}
          >
            <option value="">default</option>
            {PLAYER_ORDERS.map((order) => (
              <option key={order} value={order}>Player {order}</option>
            ))}
          </select>
        </label>
      </>
    )
  }

  const renderPlayerPanel = (order: PlayerOrder, extraClass?: string) => {
    const pColor = playerColor(order)
    const accentBorder = new Color(pColor).alpha(0.35).string()
    const headerTint = new Color(pColor).alpha(0.18).string()
    return (
      <div
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move" }}
        onDrop={(e) => {
          e.preventDefault()
          const dropped = Number(e.dataTransfer.getData("text/plain"))
          if (!Number.isNaN(dropped)) assignProperty(dropped, order)
        }}
        className={classNames(
          "rounded-xl bg-white/[0.025] border flex flex-col min-h-0 overflow-hidden shadow-lg shadow-black/20 mobile:rounded-[12px]",
          extraClass
        )}
        style={{ borderColor: accentBorder }}
      >
        {/* Player header — tinted with the player's colour */}
        <div
          className="flex items-center gap-2.5 px-4 py-3 shrink-0 border-b border-white/[0.06] mobile:gap-[8px] mobile:px-[12px] mobile:py-[8px]"
          style={{ background: `linear-gradient(to right, ${headerTint}, transparent)` }}
        >
          <span
            className="h-5 w-5 rounded-full ring-2 ring-white/20 shrink-0 shadow mobile:h-[16px] mobile:w-[16px]"
            style={{ backgroundColor: pColor }}
          />
          <span className="text-primary font-bold text-lg font-fourth mobile:text-[15px]">Player {order}</span>
        </div>

        <div className="flex flex-col gap-3 p-4 flex-1 min-h-0 overflow-y-auto mobile:gap-[10px] mobile:p-[10px]">
          {/* A landscape phone has width to spare and almost no height, so the
              fields and the card tray share one row there instead of stacking
              — that is the ~78px the owned-properties tray needs to show a
              whole card plus its house stepper. `contents` keeps the desktop
              DOM exactly as it was: the wrapper simply isn't a box there. */}
          <div className="contents mobile:flex mobile:items-start mobile:gap-[10px] mobile:shrink-0">
          {/* Starting funds, position & role — prominent labeled fields */}
          <div className="grid grid-cols-3 gap-3 shrink-0 mobile:gap-[8px] mobile:w-[360px]">
            <label className="flex flex-col gap-1.5 mobile:gap-[5px]">
              <span className={LABEL}>Starting funds</span>
              <input
                type="number"
                inputMode="numeric"
                value={fundsOf(order) ?? ""}
                onChange={(e) => setFunds(order, e.target.value === "" ? undefined : Number(e.target.value))}
                className={classNames(
                  FIELD,
                  "py-2.5 px-3 text-base font-semibold w-full mobile:py-[7px] mobile:px-[9px] mobile:text-[13px]"
                )}
                placeholder="default"
              />
            </label>
            <label className="flex flex-col gap-1.5 mobile:gap-[5px]">
              <span className={LABEL}>Position</span>
              <input
                type="number"
                inputMode="numeric"
                value={positionOf(order) ?? ""}
                onChange={(e) => setPosition(order, e.target.value === "" ? undefined : Number(e.target.value))}
                className={classNames(
                  FIELD,
                  "py-2.5 px-3 text-base font-semibold w-full mobile:py-[7px] mobile:px-[9px] mobile:text-[13px]"
                )}
                placeholder="default"
              />
            </label>
            <label className="flex flex-col gap-1.5 mobile:gap-[5px]">
              <span className={LABEL}>Role</span>
              <select
                value={roleOf(order) ?? ""}
                onChange={(e) => setRole(order, (e.target.value || undefined) as GameRoleId | undefined)}
                className={classNames(
                  FIELD,
                  "py-2.5 px-3 text-sm w-full cursor-pointer mobile:py-[7px] mobile:px-[9px] mobile:text-[13px]"
                )}
              >
                <option value="">default</option>
                {gameRoles.map((r) => (
                  <option key={r.id} value={r.id}>{r.title}</option>
                ))}
              </select>
            </label>
          </div>

          {/* Cards */}
          <div className="flex flex-col gap-2 shrink-0 mobile:gap-[6px] mobile:flex-1 mobile:min-w-0">
            <div className="flex items-center justify-between">
              <span className={LABEL}>Action cards</span>
              <button
                onClick={() => setCardPickerPlayer(order)}
                className={classNames(
                  BTN,
                  "py-1 px-2.5 text-xs border bg-white/5 hover:bg-white/10 border-white/10 text-primary/80 mobile:py-[5px] mobile:px-[10px] mobile:text-[11px]"
                )}
              >
                + Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2 min-h-[2.75rem] rounded-lg bg-black/25 border border-white/[0.05] p-2 mobile:gap-[6px] mobile:min-h-[52px] mobile:p-[6px] mobile:rounded-[8px]">
              {groupedCards(order).length === 0 && (
                <span className="text-white/25 text-xs self-center px-1 italic mobile:text-[11px]">No cards</span>
              )}
              {groupedCards(order).map(([cardId, count]) => (
                <div key={cardId} className="relative group" title={prettyCardName(cardId)}>
                  <img src={images.cards[cardId]} className="h-12 w-auto rounded-md ring-1 ring-black/40 mobile:h-[46px]" />
                  {count > 1 && (
                    <span className="absolute -top-1.5 -right-1.5 bg-[#E49E78] text-[#2a1209] text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center ring-1 ring-black/30 mobile:h-[15px] mobile:min-w-[15px] mobile:-top-[6px] mobile:-right-[6px]">
                      {count}
                    </span>
                  )}
                  {/* Nothing hovers on a phone, so the minus is simply always there. */}
                  <button
                    onClick={() => removeOneCard(order, cardId)}
                    className="absolute -bottom-1.5 -right-1.5 bg-red-600 hover:bg-red-500 text-white text-xs leading-none rounded-full h-5 w-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity ring-1 ring-black/30 mobile:opacity-100 mobile:h-[19px] mobile:w-[19px] mobile:text-[13px] mobile:-bottom-[6px] mobile:-right-[6px]"
                  >
                    −
                  </button>
                </div>
              ))}
            </div>
          </div>
          </div>

          {/* Owned properties */}
          <div className="flex flex-col gap-2 flex-1 min-h-0 mobile:gap-[6px]">
            <span className={classNames(LABEL, "shrink-0")}>
              {isMobile ? "Owned properties · tap to move" : "Owned properties · drag here"}
            </span>
            <div className="flex flex-wrap gap-2.5 min-h-[4rem] flex-1 content-start rounded-lg bg-black/25 p-2.5 border border-dashed border-white/20 overflow-y-auto mobile:gap-[8px] mobile:min-h-[88px] mobile:p-[8px] mobile:rounded-[8px]">
              {propertiesOf(order).length === 0 && (
                <span className="text-white/25 text-xs self-center px-1 italic w-full text-center mobile:text-[11px]">
                  {isMobile ? "Add them from the Properties tab" : "Drag property cards here"}
                </span>
              )}
              {propertiesOf(order).map((po) => {
                const cell = cellByOrder(po)
                if (!cell) return null
                return (
                  <div key={po} className="flex flex-col items-center gap-1 mobile:gap-[4px]">
                    <div className="relative group/owned">
                      <TestPropertyCard
                        cell={cell}
                        group={groupOf(cell)}
                        compact
                        draggable={!isMobile}
                        onTap={isMobile ? () => setAssignOrder(po) : undefined}
                        onDragStart={setDraggingOrder}
                        onDragEnd={() => setDraggingOrder(null)}
                      />
                      <button
                        onClick={() => assignProperty(po, null)}
                        className="absolute -top-2 -right-2 bg-red-600 hover:bg-red-500 text-white text-xs leading-none rounded-full h-5 w-5 flex items-center justify-center opacity-0 group-hover/owned:opacity-100 transition-opacity ring-1 ring-black/30 mobile:opacity-100 mobile:h-[19px] mobile:w-[19px] mobile:text-[13px] mobile:-top-[7px] mobile:-right-[7px]"
                        title="Unassign"
                      >
                        ×
                      </button>
                    </div>
                    <div className="flex items-center gap-1 rounded-md bg-black/40 px-1.5 py-1 mobile:gap-[3px] mobile:px-[4px] mobile:py-[3px]">
                      <button
                        onClick={() => setHouses(po, Math.max(0, housesOf(po) - 1))}
                        className="bg-white/10 hover:bg-white/20 text-white text-sm leading-none rounded h-5 w-5 flex items-center justify-center transition-colors mobile:h-[22px] mobile:w-[22px] mobile:text-[14px]"
                      >−</button>
                      <span className="text-[11px] font-semibold text-primary/90 w-9 text-center mobile:w-[34px]">
                        {housesOf(po) === MAX_HOUSES ? "Hotel" : `${housesOf(po)}h`}
                      </span>
                      <button
                        onClick={() => setHouses(po, Math.min(MAX_HOUSES, housesOf(po) + 1))}
                        className="bg-white/10 hover:bg-white/20 text-white text-sm leading-none rounded h-5 w-5 flex items-center justify-center transition-colors mobile:h-[22px] mobile:w-[22px] mobile:text-[14px]"
                      >+</button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderPalette = (extraClass?: string) => (
    <div className={classNames("flex flex-col gap-2.5 min-w-0 min-h-0 mobile:gap-[8px]", extraClass)}>
      <div className="flex items-center justify-between shrink-0 gap-2">
        <span className="text-primary/90 text-lg font-bold font-fourth mobile:text-[15px]">Properties</span>
        <span className="text-xs font-medium text-white/40 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 truncate mobile:text-[11px] mobile:px-[8px] mobile:py-[3px]">
          {map?.name}
        </span>
      </div>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault()
          const dropped = Number(e.dataTransfer.getData("text/plain"))
          if (!Number.isNaN(dropped)) assignProperty(dropped, null)
        }}
        className="flex-1 min-h-0 overflow-y-auto flex flex-wrap gap-2.5 content-start rounded-xl bg-black/25 p-3 border border-white/[0.07] mobile:gap-[8px] mobile:p-[8px] mobile:rounded-[12px]"
      >
        {propertyCells.map((cell) => (
          <div
            key={cell.order}
            className={classNames("transition-opacity duration-150", {
              "opacity-25": assignedOrders.has(cell.order),
              "opacity-100": !assignedOrders.has(cell.order) || draggingOrder === cell.order,
            })}
          >
            <TestPropertyCard
              cell={cell}
              group={groupOf(cell)}
              draggable={!isMobile}
              onTap={isMobile ? () => setAssignOrder(cell.order) : undefined}
              onDragStart={setDraggingOrder}
              onDragEnd={() => setDraggingOrder(null)}
            />
          </div>
        ))}
      </div>
      <span className="text-white/40 text-xs shrink-0 flex items-center gap-1.5 mobile:text-[10px] mobile:gap-[5px]">
        <span className="text-[#E49E78]/80 font-semibold shrink-0">Tip</span>
        {isMobile
          ? "Tap a property to give it to a player · tap an owned one to move or unassign it."
          : "Drag onto a player to assign · drag back here to unassign · dimmed cards are already owned."}
      </span>
    </div>
  )

  /* ---------------- render ---------------- */
  return (
    <>
    <div className={classNames(
      "fixed inset-0 bg-black/75 backdrop-blur-md flex items-center justify-center p-2 z-50 mobile:p-0",
      MOBILE_Z.base
    )}>
      <div
        className="relative w-full max-w-[99vw] h-[97vh] flex flex-col rounded-2xl overflow-hidden bg-gradient-to-br from-[#1a1410] to-[#0b0807] border border-white/10 ring-1 ring-black/60 shadow-2xl mobile:max-w-none mobile:rounded-none mobile:border-0 mobile:ring-0"
        // Full-bleed on a phone, measured off the real on-screen height rather
        // than 97vh so the header can never sit under the browser's URL bar.
        style={isMobile ? { height: viewportHeight } : undefined}
      >
        {/* Header */}
        <div
          className={classNames(
            "flex flex-wrap justify-between items-center gap-3 px-5 py-4 border-b border-white/[0.07] bg-white/[0.02] shrink-0",
            // A rounded phone corner or a notch eats the ends of a full-bleed
            // row, so the header keeps clear of the side insets.
            "mobile:flex-nowrap mobile:gap-[8px] mobile:py-[7px] mobile:pl-[max(12px,env(safe-area-inset-left))] mobile:pr-[max(12px,env(safe-area-inset-right))]"
          )}
        >
          <div className="flex items-center gap-3 min-w-0 mobile:gap-[8px]">
            <span className="h-9 w-1.5 rounded-full bg-gradient-to-b from-[#E49E78] to-[#883924] shrink-0 mobile:h-[24px] mobile:w-[4px]" />
            <h3 className="text-3xl font-bold text-primary font-fourth tracking-wide truncate mobile:text-[18px]">Test Settings</h3>
          </div>
          <div className="flex flex-wrap items-center gap-3 mobile:flex-nowrap mobile:gap-[8px] mobile:min-w-0">
            {/* The game-wide settings sit inline in the desktop header to save
                vertical space. There is no room for them here on a phone, so
                they get a tab of their own below. */}
            {!isMobile && renderGlobalFields(false)}
            {!isMobile && <div className="w-px h-7 bg-white/10 mx-1" />}
            <button
              onClick={() => setPresetPickerOpen(true)}
              title="Choose preset"
              className={classNames(
                BTN,
                "py-2 px-4 text-sm bg-white/5 hover:bg-white/10 border border-white/10 text-primary flex items-center gap-2.5 min-w-0 mobile:py-[6px] mobile:px-[10px] mobile:text-[12px] mobile:gap-[6px]"
              )}
            >
              {/* The word "Preset" is the first thing to go when the row has to
                  fit a phone — the name next to it says the same thing. */}
              <span className={classNames(LABEL, "shrink-0 mobile:hidden")}>Preset</span>
              <span className="font-bold max-w-[12rem] truncate mobile:max-w-[92px]">{selectedPreset?.name || "—"}</span>
              <span className="text-white/40 text-xs shrink-0">▾</span>
            </button>
            <button
              onClick={handleSaveAndClose}
              className={classNames(
                BTN,
                "py-2 px-5 text-sm text-[#2a1209] bg-gradient-to-b from-[#f0c27a] to-[#E49E78] hover:from-[#f5cd8a] hover:to-[#eba886] shadow-lg shadow-amber-900/30 whitespace-nowrap mobile:py-[7px] mobile:px-[12px] mobile:text-[12px]"
              )}
            >
              Save &amp; Use
            </button>
            <button onClick={onClose} className={CLOSE_BTN}>
              ×
            </button>
          </div>
        </div>

        {isMobile ? (
          <>
            {/* Tab bar — the phone's stand-in for the desktop's six visible
                panes. It scrolls sideways rather than wrapping, so the pane
                below keeps a fixed height whatever the map is called. */}
            <div className="flex items-center gap-[6px] shrink-0 overflow-x-auto border-b border-white/[0.07] bg-black/20 py-[6px] pl-[max(12px,env(safe-area-inset-left))] pr-[max(12px,env(safe-area-inset-right))]">
              <MobileTabButton active={mobileTab === "game"} onClick={() => setMobileTab("game")}>
                Game
              </MobileTabButton>
              {PLAYER_ORDERS.map((order) => {
                const owned = propertiesOf(order).length
                return (
                  <MobileTabButton key={order} active={mobileTab === order} onClick={() => setMobileTab(order)}>
                    <span
                      className="h-[10px] w-[10px] rounded-full ring-1 ring-white/20 shrink-0"
                      style={{ backgroundColor: playerColor(order) }}
                    />
                    P{order}
                    {/* Without a count there is no way to tell, from the tab
                        bar, which players a preset has already set up. */}
                    {owned > 0 && <span className="text-white/40 text-[10px]">{owned}</span>}
                  </MobileTabButton>
                )
              })}
              <MobileTabButton active={mobileTab === "props"} onClick={() => setMobileTab("props")}>
                Properties
                <span className="text-white/40 text-[10px]">{propertyCells.length - assignedOrders.size}</span>
              </MobileTabButton>
            </div>

            <div className="flex-1 min-h-0 flex flex-col p-[8px] pl-[max(8px,env(safe-area-inset-left))] pr-[max(8px,env(safe-area-inset-right))] pb-[max(8px,env(safe-area-inset-bottom))]">
              {mobileTab === "game" && (
                <div className="flex-1 min-h-0 overflow-y-auto rounded-[12px] bg-white/[0.025] border border-white/10 p-[12px] grid grid-cols-2 gap-[12px] content-start">
                  {renderGlobalFields(true)}
                </div>
              )}
              {typeof mobileTab === "number" && renderPlayerPanel(mobileTab, "flex-1")}
              {mobileTab === "props" && renderPalette("flex-1")}
            </div>
          </>
        ) : (
          /* Body — two columns, no outer scroll */
          <div className="flex-1 min-h-0 flex gap-3 p-3">
            {/* Left column: 4 players in 2×2 grid */}
            <div className="flex flex-col gap-2 min-w-0" style={{ flex: "0 0 62%" }}>
              <div className="grid grid-cols-2 gap-3 flex-1 min-h-0">
                {PLAYER_ORDERS.map((order) => (
                  <div key={order} className="flex min-h-0">
                    {renderPlayerPanel(order, "flex-1 min-w-0")}
                  </div>
                ))}
              </div>
            </div>

            {/* Right column: property palette */}
            {renderPalette("flex-1")}
          </div>
        )}
      </div>
    </div>

      {/* Assign sheet — the phone's replacement for dragging a card onto a panel. */}
      {assignOrder !== null && (() => {
        const target = assignOrder
        const cell = cellByOrder(target)
        const currentOwner = ownerOf(target)
        return (
          <div
            className={classNames("fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-[16px]", MOBILE_Z.sheet)}
            onClick={() => setAssignOrder(null)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-[400px] max-h-full overflow-y-auto rounded-[12px] bg-gradient-to-br from-[#1f1812] to-[#0d0a08] border border-white/10 ring-1 ring-black/60 shadow-2xl"
            >
              <div className="px-[14px] py-[10px] border-b border-white/[0.08] bg-white/[0.03]">
                <div className={LABEL}>Assign property</div>
                <div className="text-primary font-bold text-[15px] truncate">
                  {cell?.propertyDetails?.name || `#${target}`}{" "}
                  <span className="text-white/40 font-normal">#{target}</span>
                </div>
              </div>
              <div className="p-[10px] grid grid-cols-2 gap-[8px]">
                {PLAYER_ORDERS.map((order) => {
                  const owns = currentOwner === order
                  return (
                    <button
                      key={order}
                      onClick={() => {
                        assignProperty(target, order)
                        setAssignOrder(null)
                      }}
                      className={classNames(
                        BTN,
                        "flex items-center gap-[8px] border px-[10px] py-[9px] text-[13px]",
                        owns
                          ? "bg-[#E49E78]/[0.15] border-[#E49E78]/50 text-primary"
                          : "bg-white/[0.03] border-white/10 text-primary/80"
                      )}
                    >
                      <span
                        className="h-[14px] w-[14px] rounded-full ring-1 ring-white/20 shrink-0"
                        style={{ backgroundColor: playerColor(order) }}
                      />
                      Player {order}
                      {owns && <span className="ml-auto text-[#E49E78] text-[10px] font-bold">OWNS</span>}
                    </button>
                  )
                })}
              </div>
              <div className="flex gap-[8px] px-[10px] pb-[10px]">
                <button
                  onClick={() => {
                    assignProperty(target, null)
                    setAssignOrder(null)
                  }}
                  disabled={currentOwner === undefined}
                  className={classNames(
                    BTN,
                    "flex-1 px-[10px] py-[9px] text-[12px] border",
                    currentOwner === undefined
                      ? "border-white/[0.06] text-white/20"
                      : "text-red-300/80 bg-red-500/[0.07] border-red-500/20"
                  )}
                >
                  Unassign
                </button>
                <button
                  onClick={() => setAssignOrder(null)}
                  className={classNames(
                    BTN,
                    "flex-1 px-[10px] py-[9px] text-[12px] bg-white/5 border border-white/10 text-primary/80"
                  )}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Preset picker — full sub-modal with larger text */}
      {presetPickerOpen && (
        <div
          className={classNames("fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-6 mobile:p-[12px]", MOBILE_Z.sub)}
          onClick={() => setPresetPickerOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl max-h-[85vh] flex flex-col rounded-2xl overflow-hidden bg-gradient-to-br from-[#1f1812] to-[#0d0a08] border border-white/10 ring-1 ring-black/60 shadow-2xl mobile:max-w-[440px] mobile:max-h-full mobile:rounded-[12px]"
          >
            {/* Picker header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/[0.08] bg-white/[0.03] mobile:px-[12px] mobile:py-[8px]">
              <div className="flex items-center gap-3 mobile:gap-[8px]">
                <span className="h-8 w-1.5 rounded-full bg-gradient-to-b from-[#E49E78] to-[#883924] mobile:h-[22px] mobile:w-[4px]" />
                <h4 className="text-2xl font-bold text-primary font-fourth tracking-wide mobile:text-[16px]">Choose Preset</h4>
              </div>
              <button onClick={() => setPresetPickerOpen(false)} className={CLOSE_BTN}>
                ×
              </button>
            </div>

            {/* Preset list */}
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2.5 mobile:p-[10px] mobile:gap-[6px]">
              {presets.map((p) => {
                const active = p.id === selectedId
                return (
                  <button
                    key={p.id}
                    onClick={() => {
                      handleSelectPreset(p.id)
                      setPresetPickerOpen(false)
                    }}
                    className={classNames(
                      "w-full text-left px-5 py-4 rounded-xl border text-lg font-semibold flex items-center justify-between gap-3 transition-colors mobile:px-[12px] mobile:py-[9px] mobile:rounded-[10px] mobile:text-[13px] mobile:gap-[8px]",
                      active
                        ? "bg-[#E49E78]/[0.15] border-[#E49E78]/50 text-primary"
                        : "bg-white/[0.03] hover:bg-white/[0.07] border-white/10 text-primary/80"
                    )}
                  >
                    <span className="truncate">{p.name}</span>
                    {active && (
                      <span className="flex items-center gap-1.5 text-[#E49E78] text-sm font-bold shrink-0 mobile:gap-[5px] mobile:text-[11px]">
                        <span className="h-2 w-2 rounded-full bg-[#E49E78] mobile:h-[7px] mobile:w-[7px]" />
                        Active
                      </span>
                    )}
                  </button>
                )
              })}
            </div>

            {/* Preset management actions */}
            <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-white/[0.08] bg-white/[0.02] mobile:gap-[8px] mobile:px-[12px] mobile:py-[8px]">
              <button
                onClick={handleNew}
                className={classNames(
                  BTN,
                  "py-2.5 px-4 text-sm bg-white/5 hover:bg-white/10 border border-white/10 text-primary/90 whitespace-nowrap mobile:py-[7px] mobile:px-[10px] mobile:text-[12px]"
                )}
              >
                + New Preset
              </button>
              <div className="flex items-center gap-2.5 mobile:gap-[8px]">
                <button
                  onClick={handleRename}
                  className={classNames(
                    BTN,
                    "py-2.5 px-4 text-sm bg-white/5 hover:bg-white/10 border border-white/10 text-primary/80 mobile:py-[7px] mobile:px-[10px] mobile:text-[12px]"
                  )}
                >
                  Rename
                </button>
                <button
                  onClick={handleDelete}
                  className={classNames(
                    BTN,
                    "py-2.5 px-4 text-sm text-red-300/80 hover:text-red-200 bg-red-500/[0.07] hover:bg-red-500/[0.15] border border-red-500/20 mobile:py-[7px] mobile:px-[10px] mobile:text-[12px]"
                  )}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Card picker — full sub-modal so every action card is large and visible */}
      {cardPickerPlayer !== null && (() => {
        const order = cardPickerPlayer
        const counts: Partial<Record<ActionCardId, number>> = {}
        for (const c of cardsOf(order)) counts[c] = (counts[c] || 0) + 1
        const total = cardsOf(order).length
        return (
          <div
            className={classNames("fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-6 mobile:p-0", MOBILE_Z.sub)}
            onClick={() => setCardPickerPlayer(null)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-6xl max-h-[92vh] flex flex-col rounded-2xl overflow-hidden bg-gradient-to-br from-[#1f1812] to-[#0d0a08] border border-white/10 ring-1 ring-black/60 shadow-2xl mobile:max-w-none mobile:max-h-full mobile:rounded-none mobile:border-0"
              style={isMobile ? { height: viewportHeight } : undefined}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/[0.08] bg-white/[0.03] mobile:py-[7px] mobile:pl-[max(12px,env(safe-area-inset-left))] mobile:pr-[max(12px,env(safe-area-inset-right))]">
                <div className="flex items-center gap-3 flex-wrap min-w-0 mobile:flex-nowrap mobile:gap-[8px]">
                  <span className="h-8 w-1.5 rounded-full bg-gradient-to-b from-[#E49E78] to-[#883924] shrink-0 mobile:h-[22px] mobile:w-[4px]" />
                  <h4 className="text-2xl font-bold text-primary font-fourth tracking-wide mobile:text-[16px]">Action Cards</h4>
                  <span className="flex items-center gap-2 ml-1 px-3 py-1 rounded-full bg-white/5 border border-white/10 shrink-0 mobile:ml-0 mobile:gap-[6px] mobile:px-[8px] mobile:py-[3px]">
                    <span
                      className="h-3.5 w-3.5 rounded-full ring-2 ring-white/20 mobile:h-[12px] mobile:w-[12px] mobile:ring-1"
                      style={{ backgroundColor: playerColor(order) }}
                    />
                    <span className="text-primary/90 text-sm font-semibold mobile:text-[12px]">Player {order}</span>
                    <span className="text-white/40 text-xs mobile:text-[11px]">· {total} added</span>
                  </span>
                </div>
                <button onClick={() => setCardPickerPlayer(null)} className={CLOSE_BTN}>
                  ×
                </button>
              </div>

              {/* Grid of all cards. `sm:` here is a *short* screen, which a
                  landscape phone also is, so the `mobile:` step below has to
                  restate the column count for phone widths. */}
              <div className="flex-1 overflow-y-auto p-5 mobile:p-[10px] mobile:pl-[max(10px,env(safe-area-inset-left))] mobile:pr-[max(10px,env(safe-area-inset-right))]">
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 gap-4 mobile:grid-cols-6 mobile:gap-[8px]">
                  {ALL_CARD_IDS.map((cardId) => {
                    const count = counts[cardId] || 0
                    return (
                      <button
                        key={cardId}
                        onClick={() => addCard(order, cardId)}
                        title={`Add ${prettyCardName(cardId)}`}
                        className={classNames(
                          "group relative flex flex-col items-center gap-2 rounded-xl border p-3 transition-all hover:-translate-y-0.5 mobile:gap-[5px] mobile:rounded-[10px] mobile:p-[6px]",
                          count > 0
                            ? "bg-[#E49E78]/[0.12] border-[#E49E78]/40"
                            : "bg-white/[0.03] hover:bg-white/[0.07] border-white/10"
                        )}
                      >
                        <div className="relative w-full">
                          <img src={images.cards[cardId]} className="w-full rounded-lg ring-1 ring-black/40 mobile:rounded-[6px]" />
                          {count > 0 && (
                            <span className="absolute -top-2 -right-2 bg-[#E49E78] text-[#2a1209] text-sm font-bold rounded-full h-7 min-w-[1.75rem] px-1.5 flex items-center justify-center ring-2 ring-[#1f1812] mobile:h-[20px] mobile:min-w-[20px] mobile:px-[4px] mobile:text-[11px] mobile:ring-1 mobile:-top-[7px] mobile:-right-[7px]">
                              {count}
                            </span>
                          )}
                          {count > 0 && (
                            <span
                              role="button"
                              onClick={(e) => { e.stopPropagation(); removeOneCard(order, cardId) }}
                              className="absolute -bottom-2 -right-2 bg-red-600 hover:bg-red-500 text-white text-lg leading-none rounded-full h-7 w-7 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity ring-2 ring-[#1f1812] mobile:opacity-100 mobile:h-[22px] mobile:w-[22px] mobile:text-[15px] mobile:ring-1 mobile:-bottom-[7px] mobile:-right-[7px]"
                              title="Remove one"
                            >
                              −
                            </span>
                          )}
                        </div>
                        <span className="text-primary/80 text-xs font-medium text-center leading-tight line-clamp-2 mobile:text-[10px]">
                          {prettyCardName(cardId)}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end px-6 py-4 border-t border-white/[0.08] bg-white/[0.02] mobile:py-[7px] mobile:pl-[max(12px,env(safe-area-inset-left))] mobile:pr-[max(12px,env(safe-area-inset-right))] mobile:pb-[max(7px,env(safe-area-inset-bottom))]">
                <button
                  onClick={() => setCardPickerPlayer(null)}
                  className={classNames(
                    BTN,
                    "py-2.5 px-6 text-sm text-[#2a1209] bg-gradient-to-b from-[#f0c27a] to-[#E49E78] hover:from-[#f5cd8a] hover:to-[#eba886] shadow-lg shadow-amber-900/30 mobile:py-[7px] mobile:px-[18px] mobile:text-[12px]"
                  )}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )
      })()}
    </>
  )
}

export default TestSettingsModal
